// @owners { team: patients }
import { ActionSheetContext, ActionSheetV2 } from '@alto/design-system';
import { MenuOptions, PricingPaymentsActionSheet } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { ASSISTANT_VIEWED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import {
  sendAssistantMedicationActionChosen,
  sendAssistantViewedEvent,
} from '~shared/features/alto-assistant/analytics/helpers';
import {
  ASSISTANT_CATEGORIES,
  ASSISTANT_CHAT_ORIGINS,
  ASSISTANT_MEDICATIONS_ACTIONS,
  ASSISTANT_MESSAGE_SUBJECTS,
  type ActionSheetProviderStackCacheKey,
  type AssistantMedicationsAction,
  MESSAGE_PROMPTS,
} from '~shared/features/alto-assistant/constants';
import { PROCESSING } from '~shared/features/my-meds/constants';
import { makeGetMedicationTagKey } from '~shared/features/my-meds/selectors/getMedicationTagKey';
import { partitionMedicationsByActiveState } from '~shared/features/my-meds/selectors/partitionMedicationsByActiveState';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { shallowEqual, useDispatchShared, useSelectorShared } from '~shared/store';
import { type ReduxStateShared } from '~shared/types';

export const MedicationsActionSheet = () => {
  const { navigate } = useNavigation();

  const dispatch = useDispatchShared();
  const { saveStack, setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const { active, needsAttention } = useSelectorShared(partitionMedicationsByActiveState);
  const medications = needsAttention.concat(active);
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const processingMedications = useSelectorShared(
    (state: ReduxStateShared) =>
      medications.filter((medication) => {
        const { key } = makeGetMedicationTagKey(state, {
          medication,
          userID: medication.userID,
          nextAvailableDateAddressID,
        });
        return key === PROCESSING;
      }),
    shallowEqual,
  );

  useEffect(() => {
    dispatch(
      sendAssistantViewedEvent(ASSISTANT_VIEWED_EVENTS.MEDICATIONS_ACTION_SHEET, {
        processingMedicationsCount: processingMedications.length,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNavigate = (navigate: () => void, action: AssistantMedicationsAction) => {
    dispatch(sendAssistantMedicationActionChosen(action));
    navigate();
  };

  const options = [
    {
      iconName: 'rxchart' as const,
      text: 'Status of new prescription',
      onPress: () => {
        onNavigate(() => {
          const stackCacheKey = 'assistant-medications';
          saveStack<ActionSheetProviderStackCacheKey>(stackCacheKey);
          closeActionSheet();
          navigate('RouteAssistantMedSelection', {
            showOnlyProcessingMeds: true,
            stackCacheKey,
            category: 'medications.status_of_new_prescription',
          });
        }, ASSISTANT_MEDICATIONS_ACTIONS.STATUS_OF_NEW_PRESCRIPTION);
      },
      hide: processingMedications.length === 0,
    },
    {
      iconName: 'pricetag' as const,
      text: 'Pricing',
      onPress: () => {
        onNavigate(() => {
          setActiveActionSheet(<PricingPaymentsActionSheet />);
        }, ASSISTANT_MEDICATIONS_ACTIONS.PRICING);
      },
    },
    {
      iconName: 'questionmark' as const,
      text: 'Something else',
      onPress: () => {
        onNavigate(() => {
          closeActionSheet();
          navigate('RouteAssistantMessage', {
            messagePrompt: MESSAGE_PROMPTS.HELP_WITH_MEDICATION,
            origin: ASSISTANT_CHAT_ORIGINS.MEDICATIONS,
            category: `${ASSISTANT_CATEGORIES.MEDICATIONS}.${ASSISTANT_MEDICATIONS_ACTIONS.SOMETHING_ELSE}`,
            subject: ASSISTANT_MESSAGE_SUBJECTS.MEDICATION,
          });
        }, ASSISTANT_MEDICATIONS_ACTIONS.SOMETHING_ELSE);
      },
    },
  ];

  return (
    <ActionSheetV2
      title="Medications"
      analyticsName="assistant medications"
    >
      <MenuOptions options={options} />
    </ActionSheetV2>
  );
};
